import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useEligibilityData } from "../useEligibilityData";
import Navbar from "../../../components/molecules/Navbar";
import Footer from "../../../components/molecules/Footer";
import Loader from "../../../components/atoms/Loader";
import styles from "../index.module.css";

function EligibilitySanctionLetter() {
  const navigate = useNavigate();
  const location = useLocation();
  const userData = useEligibilityData();

  const sanctionLetterUrl = location.state?.sanctionLetterUrl as string | null;

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [rawS3Url, setRawS3Url] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const token = sessionStorage.getItem("eligibility_auth_token");
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        let s3Url = sanctionLetterUrl;
        if (!s3Url) {
          const appId = (userData as any)?.applicationId
            || JSON.parse(sessionStorage.getItem("eligibility_data") || "{}")?.applicationId;
          if (!appId) throw new Error("No applicationId");
          const detailsRes = await fetch(
            `${process.env.REACT_APP_DASHBOARD_URL}/admin/application/sanction-details?applicationId=${appId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const detailsJson = await detailsRes.json();
          s3Url = detailsJson?.data?.sanctionLetterUrl;
          if (!s3Url) throw new Error("No sanctionLetterUrl");
        }
        setRawS3Url(s3Url);
        const path = s3Url.split("amazonaws.com/")[1];
        const res = await fetch(
          `${process.env.REACT_APP_DASHBOARD_URL}/file/url-content?path=${encodeURIComponent(path)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        if (data?.data) setPdfUrl(data.data);
        else throw new Error("No presigned URL");
      } catch {
        setError("Unable to load the document");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [reloadKey]); // eslint-disable-line

  const handleContinue = () => {
    if (!isChecked || submitting) return;
    setSubmitting(true);
    setSubmitError("");
    const token = sessionStorage.getItem("eligibility_auth_token");
    fetch(`${process.env.REACT_APP_DASHBOARD_URL}/application/send-tnc-mail`, {
      method: "POST",
      headers: { "content-type": "application/json", Authorization: `Bearer ${token}` },
      redirect: "follow",
      body: JSON.stringify({ user: btoa(`${userData?.userId} ${userData?.applicationId}`) }),
    })
      .then((res) => {
        if (!res.ok) throw new Error("send-tnc-mail failed");
        navigate("/eligibility/selfie", { state: { data: userData } });
      })
      .catch(() => setSubmitError("Something went wrong. Please try again."))
      .finally(() => setSubmitting(false));
  };

  return (
    <div className={styles.body}>
      <div className={styles.backdrop}>
        <Navbar />
        <div className={styles.main}>
          <div className={styles.cardWrapper}>
            <div className={styles.container} style={{ padding: "1rem 1.25rem 0.5rem" }}>

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                  <div style={{
                    width: "2.4rem", height: "2.4rem", borderRadius: "50%",
                    background: "#F4E5DD", display: "flex", alignItems: "center",
                    justifyContent: "center", flexShrink: 0,
                  }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M14 2H6C4.895 2 4 2.895 4 4V20C4 21.105 4.895 22 6 22H18C19.105 22 20 21.105 20 20V8L14 2Z" fill="#d32028" opacity="0.15" stroke="#d32028" strokeWidth="1.5" strokeLinejoin="round" />
                      <polyline points="14,2 14,8 20,8" stroke="#d32028" strokeWidth="1.5" strokeLinejoin="round" />
                      <line x1="8" y1="13" x2="16" y2="13" stroke="#d32028" strokeWidth="1.5" strokeLinecap="round" />
                      <line x1="8" y1="17" x2="13" y2="17" stroke="#d32028" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </div>
                  <p className={styles.cardTitle} style={{ margin: 0 }}>Sanction Letter</p>
                </div>
                {pdfUrl && (
                  <button
                    onClick={async () => {
                      if (downloading || !rawS3Url) return;
                      setDownloading(true);
                      try {
                        const token = sessionStorage.getItem("eligibility_auth_token");
                        const path = rawS3Url.split("amazonaws.com/")[1];
                        const res = await fetch(
                          `${process.env.REACT_APP_DASHBOARD_URL}/file/url-content-sl?path=${encodeURIComponent(path)}`,
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        const data = await res.json();
                        if (data?.data) {
                          const link = document.createElement("a");
                          link.href = data.data;
                          link.download = "sanction-letter.pdf";
                          link.click();
                        }
                      } finally {
                        setDownloading(false);
                      }
                    }}
                    style={{
                      width: "36px", height: "36px", flexShrink: 0,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      background: "#d32028", border: "none", borderRadius: "10px",
                      cursor: "pointer"
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M12 3V15M12 15L8 11M12 15L16 11" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M4 19H20" stroke="white" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </button>
                )}
              </div>

              {loading && (
                <div style={{ display: "flex", justifyContent: "center", padding: "2rem 0" }}>
                  <Loader />
                </div>
              )}

              {!loading && error && (
                <div style={{ textAlign: "center", padding: "2rem 0" }}>
                  <p style={{ color: "#d32028", fontSize: "0.85rem", margin: "0 0 0.75rem" }}>{error}</p>
                  <button
                    onClick={() => setReloadKey((k) => k + 1)}
                    style={{
                      width: "auto",
                      margin: 0,
                      padding: "0.5rem 1.5rem",
                      background: "#fff",
                      color: "#d32028",
                      border: "1.5px solid #d32028",
                      borderRadius: "60px",
                      fontSize: "0.85rem",
                      fontWeight: 700,
                      fontFamily: "Outfit, sans-serif",
                      cursor: "pointer",
                    }}
                  >
                    Retry
                  </button>
                </div>
              )}

              {!loading && pdfUrl && (
                <iframe
                  src={`${pdfUrl}#toolbar=0&navpanes=0&view=FitH`}
                  title="Sanction Letter"
                  style={{
                    width: "100%",
                    height: "52vh",
                    border: "1px solid #e8e8e8",
                    borderRadius: "10px",
                    display: "block",
                  }}
                />
              )}

              {!loading && (
                <div style={{ marginTop: "0.75rem" }}>
                  <p style={{ fontSize: "0.75rem", color: "#555", textAlign: "center", margin: 0 }}>
                    Your loan is being sanctioned by:
                  </p>
                  <p style={{ fontSize: "0.95rem", fontWeight: 600, color: "#222", textAlign: "center", margin: "0.15rem 0 0.6rem" }}>
                    Monk Capital Private Limited
                  </p>
                  <label style={{ position: "relative", display: "flex", alignItems: "flex-start", gap: "0.55rem", background: "#fdecea", border: "1px solid #f6cfcc", borderRadius: "10px", padding: "0.7rem 0.85rem", width: "100%", margin: "0.8rem 0 0", boxSizing: "border-box", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => setIsChecked(e.target.checked)}
                      style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{ width: "18px", height: "18px", borderRadius: "5px", border: "2px solid #d32028", background: isChecked ? "#d32028" : "transparent", flexShrink: 0, marginTop: "1px", display: "flex", alignItems: "center", justifyContent: "center", boxSizing: "border-box" }}>
                      {isChecked && (
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                          <path d="M5 12l5 5L20 7" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </span>
                    <span style={{ display: "block", textAlign: "left", fontSize: "0.72rem", color: "#555", lineHeight: 1.5 }}>
                      I have read and understood the <b>Sanction Letter</b> issued by the lender and I accept the same.
                    </span>
                  </label>
                  {submitError && (
                    <p style={{ color: "#d32028", fontSize: "0.75rem", margin: "0.4rem 0 0" }}>{submitError}</p>
                  )}
                </div>
              )}
            </div>

            <div style={{ padding: "0.75rem 1.25rem 1rem", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: "0.6rem" }}>
              <button
                onClick={handleContinue}
                disabled={!isChecked || submitting}
                style={{
                  width: "70%",
                  padding: "0.85rem 1.5rem",
                  background: !isChecked || submitting ? "#c1c1c1" : "#d32028",
                  color: "#fff",
                  border: "none",
                  borderRadius: "60px",
                  fontSize: "0.95rem",
                  fontWeight: 700,
                  fontFamily: "Outfit, sans-serif",
                  cursor: !isChecked || submitting ? "not-allowed" : "pointer",
                  letterSpacing: "0.3px",
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem"
                }}
              >
                {submitting ? "Please wait..." : (
                  <>
                    Accept & Continue
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12H19M13 6L19 12L13 18" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </>
                )}
              </button>
              {process.env.REACT_APP_CURRENT !== 'prod' && (
                <button
                  onClick={() => navigate("/eligibility/sanction-details", { state: { data: userData } })}
                  style={{ background: "none", border: "none", color: "#bbb", fontSize: "0.78rem", cursor: "pointer", fontFamily: "Outfit, sans-serif", textDecoration: "underline" }}
                >
                  Skip
                </button>
              )}
            </div>
            <Footer light />
          </div>
        </div>
      </div>
    </div>
  );
}

export default EligibilitySanctionLetter;
